import { SAGE } from '../theme/colors'
export { SAGE }
import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ALL_CARDS_REGISTRY } from '../pages/cardRegistry'

const COLS = [2, 3, 4, 6, 12]
const DEVICES = [
  { mode: 'mobile',  icon: 'phone_iphone', label: 'Mobile'  },
  { mode: 'tablet',  icon: 'tablet_mac',   label: 'Tablet'  },
  { mode: 'desktop', icon: 'computer',     label: 'Desktop' },
]

function currentDevice() {
  const w = window.innerWidth
  if (w < 768)  return 'mobile'
  if (w < 1024) return 'tablet'
  return 'desktop'
}

const CARD_POOL = Object.values(ALL_CARDS_REGISTRY)

export function PageCardPicker({ order, onAdd, onRemove, onReorder, cardConfigs, onConfigChange, onClose, isAdmin = false }) {
  const [search, setSearch]         = useState('')
  const [localOrder, setLocalOrder] = useState(order)
  const [openConfig, setOpenConfig] = useState(null)
  const closeTimer = useRef(null)
  const dragSrc    = useRef(null)
  const ref        = useRef(null)

  useEffect(() => { setLocalOrder(order) }, [order])

  useEffect(() => {
    function handler(e) {
      if (!ref.current?.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const canDrag = !search

  const matchSearch = c => !search
    || c.label.toLowerCase().includes(search.toLowerCase())
    || (c.description ?? '').toLowerCase().includes(search.toLowerCase())

  const addedCards = localOrder.map(id => ALL_CARDS_REGISTRY[id]).filter(Boolean).filter(matchSearch)
  const notAdded   = CARD_POOL.filter(c => !localOrder.includes(c.id)).filter(c => !c.adminOnly || isAdmin).filter(matchSearch)
  const hasNoMatch = addedCards.length === 0 && notAdded.length === 0

  function handleDragStart(e, idx) {
    dragSrc.current = idx
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleDragOver(e, idx) {
    e.preventDefault()
    if (dragSrc.current === null || dragSrc.current === idx) return
    const next = [...localOrder]
    const [moved] = next.splice(dragSrc.current, 1)
    next.splice(idx, 0, moved)
    dragSrc.current = idx
    setLocalOrder(next)
  }

  function handleDrop(e) {
    e.preventDefault()
    onReorder(localOrder)
    dragSrc.current = null
  }

  function handleDragEnd() { dragSrc.current = null }

  return (
    <div
      ref={ref}
      className="absolute right-0 mt-2 z-50 flex flex-col"
      style={{
        width: 340,
        maxWidth: 'calc(100vw - 1rem)',
        background: '#1f201f',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        overflow: 'hidden',
      }}
    >
      {/* Search */}
      <div className="px-3 pt-2.5 pb-2 border-b flex items-center gap-2" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '0.9rem', color: '#555a54' }}>search</span>
        <input
          autoFocus
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search cards…"
          className="flex-1 bg-transparent outline-none text-xs"
          style={{ color: '#c3c8bf', fontSize: '16px' }}
        />
        <span className="font-mono text-[10px] flex-shrink-0 px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.05)', color: '#8d928a' }}>
          {order.length} / {CARD_POOL.length}
        </span>
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: 480 }}>
        {hasNoMatch && (
          <p className="text-xs text-center py-6 text-outline-faint">No matches</p>
        )}

        {addedCards.length > 0 && (
          <div>
            <p className="px-3 pt-2.5 pb-1 font-label uppercase tracking-wider" style={{ fontSize: '9px', color: '#3d3e3d' }}>On this page</p>
            {addedCards.map(card => {
              const idx = localOrder.indexOf(card.id)
              const cfg = cardConfigs[card.id] ?? {}
              const isConfigOpen = openConfig === card.id
              const showGear = currentDevice() !== 'mobile'
              return (
                <div key={card.id}>
                  <div
                    draggable={canDrag}
                    onDragStart={canDrag ? e => handleDragStart(e, idx) : undefined}
                    onDragOver={canDrag ? e => handleDragOver(e, idx) : undefined}
                    onDrop={canDrag ? handleDrop : undefined}
                    onDragEnd={canDrag ? handleDragEnd : undefined}
                    className="flex items-center gap-2 px-3 py-2 transition-all"
                    style={{ color: '#c3c8bf', cursor: canDrag ? 'grab' : 'default', background: isConfigOpen ? 'rgba(255,255,255,0.04)' : 'transparent', borderLeft: isConfigOpen ? `2px solid ${SAGE}` : '2px solid transparent' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderLeft = `2px solid ${SAGE}` }}
                    onMouseLeave={e => { if (!isConfigOpen) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderLeft = '2px solid transparent' } }}
                  >
                    {canDrag && (
                      <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '1rem', color: '#3d3e3d', cursor: 'grab' }}>drag_indicator</span>
                    )}
                    <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '1rem', color: SAGE, fontVariationSettings: "'FILL' 1" }}>{card.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{card.label}</p>
                    </div>
                    {showGear && (
                      <button
                        title="Configure columns"
                        style={{ background: isConfigOpen ? 'rgba(176,206,174,0.1)' : 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0, color: isConfigOpen ? SAGE : 'rgba(195,200,191,0.25)', transition: 'color 0.15s', display: 'flex', borderRadius: 4 }}
                        onMouseEnter={e => { clearTimeout(closeTimer.current); setOpenConfig(card.id); e.currentTarget.style.color = SAGE }}
                        onMouseLeave={e => { closeTimer.current = setTimeout(() => setOpenConfig(null), 120); if (!isConfigOpen) e.currentTarget.style.color = 'rgba(195,200,191,0.25)' }}
                        onClick={() => setOpenConfig(v => v === card.id ? null : card.id)}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '0.95rem' }}>settings</span>
                      </button>
                    )}
                    <button
                      onClick={() => onRemove(card.id)}
                      title="Remove"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0, color: 'rgba(241,162,134,0.4)', transition: 'color 0.15s', display: 'flex' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#f1a286'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(241,162,134,0.4)'}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '0.95rem' }}>delete</span>
                    </button>
                  </div>

                  <AnimatePresence>
                    {showGear && isConfigOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: 'hidden' }}
                        onMouseEnter={() => { clearTimeout(closeTimer.current); setOpenConfig(card.id) }}
                        onMouseLeave={() => { closeTimer.current = setTimeout(() => setOpenConfig(null), 120) }}
                      >
                        {(() => {
                          const dev = DEVICES.find(d => d.mode === currentDevice())
                          const key = `${dev.mode}Cols`
                          const defaultCols = dev.mode === 'mobile' ? 12 : card.cols
                          const current = cfg[key] ?? defaultCols
                          return (
                            <div style={{ background: 'rgba(255,255,255,0.03)', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', borderLeft: `2px solid ${SAGE}`, padding: '6px 12px 6px 36px' }}>
                              <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '0.85rem', color: '#555a54' }} title={dev.label}>{dev.icon}</span>
                                <div className="flex gap-0.5">
                                  {COLS.map(c => (
                                    <button key={c} onClick={() => onConfigChange(card.id, key, c)} className="rounded font-mono transition-all"
                                      style={{ padding: '2px 6px', fontSize: '10px', background: current === c ? SAGE : 'rgba(255,255,255,0.05)', color: current === c ? '#1f201f' : '#555a54', border: 'none', cursor: 'pointer' }}>
                                      {c}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )
                        })()}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        )}

        {addedCards.length > 0 && notAdded.length > 0 && (
          <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 12px' }} />
        )}

        {notAdded.length > 0 && (
          <div>
            {(addedCards.length > 0 || search) && (
              <p className="px-3 pt-2 pb-1 font-label uppercase tracking-wider" style={{ fontSize: '9px', color: '#3d3e3d' }}>Available</p>
            )}
            {notAdded.map(card => (
              <button key={card.id} onClick={() => onAdd(card.id)}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all"
                style={{ color: '#c3c8bf', background: 'none', border: 'none', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '1rem', color: SAGE, fontVariationSettings: "'FILL' 1" }}>{card.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{card.label}</p>
                  {card.description && <p className="text-[10px] truncate mt-0.5 text-outline-faint">{card.description}</p>}
                </div>
                <span className="material-symbols-outlined flex-shrink-0" style={{ fontSize: '0.85rem', color: '#555a54' }}>add</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export function CustomizeButton({ pickerOpen, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group flex items-center px-3 py-2 rounded-lg text-xs font-semibold transition-all"
      style={{
        background: pickerOpen ? 'rgba(176,206,174,0.15)' : 'rgba(176,206,174,0.08)',
        color: SAGE,
        border: '1px solid rgba(176,206,174,0.2)',
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>tune</span>
      <span className="overflow-hidden max-w-0 group-hover:max-w-xs opacity-0 group-hover:opacity-100 group-hover:ml-1.5 transition-all duration-300 whitespace-nowrap">
        Customize page
      </span>
    </button>
  )
}
